import axios from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { NAMESPACES } from '../configuration';
const BASE_URL = NAMESPACES.management_api.mesas_info;

// Helper function to configure the Axios instance
const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Cached API wrapper with very short TTL for real-time mesa data
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'management_api',
  group: 'mesas_info',
  defaultTtl: '20s',
  hashLargeSegments: true,
  emitMeta: true,
});

// /GetAllMesasInfo

export const getAllMesasInfo = async () => {
    return await retryWithBackoff(() =>
      cachedApi.get('/GetAllMesasInfo')
    );
};

// /GetMesaInfoById/<int:mesa_id>

export const getMesaInfoById = async (mesa_id: number) => {
    return await retryWithBackoff(() =>
      cachedApi.get(`/GetMesaInfoById/${mesa_id}`)
    );
};

// /UpdateMesaInfo/<int:mesa_id> that also receives payload

export const updateMesaInfo = async (mesa_id: number, payload: any) => {
    const res = await retryWithBackoff(() =>
      api.put(`/UpdateMesaInfo/${mesa_id}`, payload)
    );
    await cachedApi.clear();
    return res;
};

export const updateMesaCurrentPeople = async (mesa_id: number, current_people: number) => {
    const res = await retryWithBackoff(() =>
      api.put(`/UpdateMesaCurrentPeople/${mesa_id}`, { current_people })
    );
    await cachedApi.clear();
    return res;
};

// /GetVirtualMesasByLocalId/<int:local_id>

export const getVirtualMesasByLocalId = async (local_id: number) => {
    return await retryWithBackoff(() =>
      cachedApi.get(`/GetVirtualMesasByLocalId/${local_id}`)
    );
};

// /SetMesaAsVirtual/<int:mesa_id>/<int:local_id>

export const setMesaAsVirtual = async (mesa_id: number, local_id: number) => {
    const res = await retryWithBackoff(() =>
      api.post(`/SetMesaAsVirtual/${mesa_id}/${local_id}`)
    );
    await cachedApi.clear();
    return res;
};

// /RemoveVirtualMesa/<int:mesa_id>

export const removeVirtualMesa = async (mesa_id: number) => {
    const res = await retryWithBackoff(() =>
      api.delete(`/RemoveVirtualMesa/${mesa_id}`)
    );
    await cachedApi.clear();
    return res;
};

/**
 * @ns.route('/GetTotalChargesByLocalId/<int:local_id>')
@ns.param('local_id', 'The local identifier')
class GetTotalChargesByLocalId(Resource):
    @ns.doc('get_total_charges_by_local_id')
    @jwt_required()
    def get(self, local_id):
        '''
        Get total charges summary for a local.
        Returns mesas that are in use (used=True) but haven't been charged yet (charged=False).
        Includes distribution by user_name.
        '''
        try:
            # Query mesas_info joined with salones_mesas to filter by local_id
            # Only include mesas where used=True and charged=False
            mesas_in_use = db.session.query(MesaInfo).join(
                SalonMesa, MesaInfo.mesa_id == SalonMesa.mesa_id
            ).filter(
                SalonMesa.local_id == local_id,
                MesaInfo.used == True,
                MesaInfo.charged == False
            ).all()
            
            total_mesas = len(mesas_in_use)
            total_charged = sum(float(mesa.total_charges or 0) for mesa in mesas_in_use)
            
            # Build distribution by user_name
            user_distribution = {}
            for mesa in mesas_in_use:
                user_name = mesa.user_name or 'Sin asignar'
                if user_name not in user_distribution:
                    user_distribution[user_name] = {
                        'user_name': user_name,
                        'user_id': mesa.user_id,
                        'mesa_count': 0,
                        'total_charges': 0.0
                    }
                user_distribution[user_name]['mesa_count'] += 1
                user_distribution[user_name]['total_charges'] += float(mesa.total_charges or 0)
            
            # Convert to list for the response
            distribution_list = list(user_distribution.values())
            
            return {
                'total_mesas': total_mesas,
                'total_charges': round(total_charged, 2),
                'distribution_by_user': distribution_list
            }, 200
            
        except Exception as e:
            logger.error(f"[GetTotalChargesByLocalId] Error: {str(e)}")
            ns.abort(500, str(e), status="Could not retrieve charges summary", statusCode="500")
 */

export const getTotalChargesByLocalId = async (local_id: number) => {
    return await retryWithBackoff(() =>
      cachedApi.get(`/GetTotalChargesByLocalId/${local_id}`)
    );
};

